import type {
  Integration,
  IntegrationCategory,
  IntegrationFilter,
} from "./integrationTypes";
import { CATEGORY_ORDER } from "./integrationMockData";

/**
 * Pure helpers for the Integration transactions page.
 *
 * All functions are side-effect free, framework agnostic, and safe to
 * import from any Integration Transactions component.
 */

/** Filter integrations by category and/or search term. */
export function filterIntegrations(
  integrations: Integration[],
  filter: IntegrationFilter,
): Integration[] {
  const term = filter.search.trim().toLowerCase();
  return integrations.filter((integration) => {
    if (filter.category !== "all" && integration.category !== filter.category) {
      return false;
    }
    if (term && !integration.name.toLowerCase().includes(term)) {
      return false;
    }
    return true;
  });
}

/** Return only integrations with status === "connected". */
export function getConnected(integrations: Integration[]): Integration[] {
  return integrations.filter((i) => i.status === "connected");
}

/** Return only integrations with status === "available". */
export function getAvailable(integrations: Integration[]): Integration[] {
  return integrations.filter((i) => i.status === "available");
}

/**
 * Group integrations by category, preserving CATEGORY_ORDER. Empty
 * categories are omitted from the result.
 */
export function groupByCategory(
  integrations: Integration[],
): Array<{ category: IntegrationCategory; items: Integration[] }> {
  const map = new Map<IntegrationCategory, Integration[]>();
  for (const integration of integrations) {
    const existing = map.get(integration.category) ?? [];
    existing.push(integration);
    map.set(integration.category, existing);
  }
  return CATEGORY_ORDER.filter((cat) => map.has(cat)).map((cat) => ({
    category: cat,
    items: map.get(cat) ?? [],
  }));
}

/**
 * Format an ISO datetime as a short relative string.
 * e.g. "just now", "12 min ago", "2 hr ago", "3 days ago", "Oct 15"
 */
export function formatLastSyncedRelative(iso: string): string {
  const target = new Date(iso).getTime();
  const now = Date.now();
  const diffSec = Math.round((now - target) / 1000);
  const diffMin = Math.round(diffSec / 60);
  const diffHr = Math.round(diffMin / 60);
  const diffDay = Math.round(diffHr / 24);

  if (diffSec < 60) return "just now";
  if (diffMin < 60) return `${diffMin} min ago`;
  if (diffHr < 24) return `${diffHr} hr ago`;
  if (diffDay < 7) return `${diffDay} days ago`;

  const d = new Date(iso);
  return d.toLocaleDateString("en-CA", { month: "short", day: "numeric" });
}